/**
 * Geolocation service — IP-based country detection.
 *
 * Uses ipapi.co (free tier: 1000 requests/day, no auth, returns
 * country_code as ISO 3166-1 alpha-2). Fails gracefully — returns null on
 * any error including rate-limit / network block / unsupported region.
 *
 * Caches the resolved country in localStorage for 24h to avoid repeated API
 * calls on every page load. Cache key: 'mrt-geo-country'.
 *
 * Also provides countryToEntityCode() + detectEntityFromIp() — the high-level
 * helpers consumed by useRegion.
 */

import countryToEntityData from '@data/countryToEntity.json';

const CACHE_KEY = 'mrt-geo-country';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Build the ISO -> entity lookup once at module load. The country mapping JSON
// contains ambiguous + conflicting entries; we take the FIRST non-ambiguous
// entry per ISO as the authoritative mapping. If there is no non-ambiguous
// entry, fall back to the first ambiguous one. Order in the JSON reflects
// row order in the source Excel.
const MAPPING = new Map();
for (const m of countryToEntityData.mappings) {
  if (!m.isoCountryCode || !m.entityCode) continue;
  const iso = m.isoCountryCode.toUpperCase();
  const existing = MAPPING.get(iso);
  if (!existing) {
    MAPPING.set(iso, { entityCode: m.entityCode, ambiguous: !!m.ambiguous });
  } else if (existing.ambiguous && !m.ambiguous) {
    // Prefer a non-ambiguous mapping over an ambiguous one
    MAPPING.set(iso, { entityCode: m.entityCode, ambiguous: false });
  }
}

const DEFAULT_ENTITY = countryToEntityData.default || 'SE';

/**
 * Detect the user's country (ISO 3166-1 alpha-2) from their IP.
 * Returns null on any error. Caches result in localStorage for 24h.
 */
export async function detectCountryFromIp() {
  // Check localStorage cache first
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const cached = JSON.parse(raw);
      if (
        cached &&
        cached.country &&
        typeof cached.timestamp === 'number' &&
        Date.now() - cached.timestamp < CACHE_TTL_MS
      ) {
        return cached.country;
      }
    }
  } catch {
    // fall through — corrupt cache, treat as miss
  }

  try {
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) return null;
    const data = await res.json();
    const country = data && data.country_code;
    if (!country || typeof country !== 'string') return null;
    try {
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ country, timestamp: Date.now() })
      );
    } catch {
      // private mode / quota — non-fatal
    }
    return country;
  } catch {
    return null;
  }
}

/**
 * Clear the geolocation cache. Used by tests and by manual override flows.
 */
export function clearGeoCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Convert an ISO 3166-1 alpha-2 country code to a Sandvik sales entity code.
 * Falls back to the default (Sweden = SE) when no mapping exists.
 */
export function countryToEntityCode(isoCountry) {
  if (!isoCountry) return DEFAULT_ENTITY;
  const entry = MAPPING.get(String(isoCountry).toUpperCase());
  return entry ? entry.entityCode : DEFAULT_ENTITY;
}

/**
 * High-level: detect country from IP then resolve to a Sandvik entity code.
 * Always returns a value (defaults to SE on any failure).
 */
export async function detectEntityFromIp() {
  const country = await detectCountryFromIp();
  return countryToEntityCode(country);
}

export const __DEFAULT_ENTITY__ = DEFAULT_ENTITY;
